import type { SiteMap, SiteMapLink, SiteMapPage } from '@innerloopco/loopcms-core'

/** Collections with more pages than this start folded, in both views. */
export const FOLD_OVER = 12

export interface TreeNode {
  page: SiteMapPage
  children: TreeNode[]
}

/**
 * The site as a tree by address: each page sits under the longest address
 * above it that is also a page ("/journal/rye" under "/journal", "/about/team"
 * under "/" when there is no "/about"). Siblings are ordered by address.
 */
export function buildTree(pages: SiteMapPage[]): TreeNode {
  const byPath = new Map(pages.map((page) => [page.path, { page, children: [] as TreeNode[] }]))
  const root = byPath.get('/') ?? { page: { id: '/', kind: 'home', title: 'Home', path: '/', collection: null, entryId: null, status: null }, children: [] }
  for (const node of [...byPath.values()].sort((a, b) => a.page.path.localeCompare(b.page.path))) {
    if (node === root) continue
    parentOf(node.page.path, byPath, root).children.push(node)
  }
  return root
}

function parentOf(path: string, byPath: Map<string, TreeNode>, root: TreeNode): TreeNode {
  let at = path
  while (at.includes('/') && at !== '/') {
    at = at.slice(0, at.lastIndexOf('/')) || '/'
    const found = byPath.get(at)
    if (found && at !== path) return found
  }
  return root
}

/** The links into and out of one page. */
export function linksFor(map: SiteMap, id: string): { from: SiteMapLink[]; to: SiteMapLink[] } {
  return { from: map.links.filter((l) => l.to === id), to: map.links.filter((l) => l.from === id) }
}

/** Depth-first, skipping the children of collapsed pages. */
export function flatten(node: TreeNode, collapsed: Set<string>, depth = 1): Array<{ node: TreeNode; depth: number }> {
  const out = [{ node, depth }]
  if (!collapsed.has(node.page.path)) for (const child of node.children) out.push(...flatten(child, collapsed, depth + 1))
  return out
}

/** Collections too big to show open at first. */
export function initiallyFolded(tree: TreeNode): Set<string> {
  const folded = new Set<string>()
  const walk = (node: TreeNode) => {
    if (node.page.kind === 'listing' && node.children.length > FOLD_OVER) folded.add(node.page.path)
    node.children.forEach(walk)
  }
  walk(tree)
  return folded
}
